<template>
  <div class="dl-page">
    <!-- ───────────── Top bar ───────────── -->
    <header class="topbar">
      <router-link to="/" class="logo-link">
        <img src="/static/v2/logo.png" alt="Momfident" />
      </router-link>
      <div class="right">
        <router-link to="/about" class="nav-link">{{ $t('download-v2-nav-about') }}</router-link>
        <router-link to="/survey" class="nav-link">{{ $t('download-v2-nav-survey') }}</router-link>
        <router-link to="/partnership" class="nav-link">{{ $t('download-v2-nav-partnership') }}</router-link>
        <button class="lang" type="button" @click="toggleLang">{{ locale === 'bg' ? 'EN' : 'БГ' }}</button>
      </div>
      <button class="hamburger" type="button" aria-label="Menu" :aria-expanded="menuOpen" @click="menuOpen = !menuOpen">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 12H21" stroke="#6B6B9C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3 6H21" stroke="#6B6B9C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3 18H21" stroke="#6B6B9C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </header>

    <div class="mobile-menu" :class="{ active: menuOpen }">
      <button class="lang" type="button" @click="toggleLang(); menuOpen = false">{{ locale === 'bg' ? 'EN' : 'БГ' }}</button>
      <router-link to="/about" @click="menuOpen = false">{{ $t('download-v2-nav-about') }}</router-link>
      <router-link to="/survey" @click="menuOpen = false">{{ $t('download-v2-nav-survey') }}</router-link>
      <router-link to="/partnership" @click="menuOpen = false">{{ $t('download-v2-nav-partnership') }}</router-link>
    </div>

    <!-- ───────────── Hero ───────────── -->
    <section class="hero dl-container">
      <div class="eyebrow">
        <span class="dot"></span>
        <span>{{ $t('download-v2-hero-eyebrow') }}</span>
      </div>
      <h1>{{ $t('download-v2-hero-title-1') }} <em>{{ $t('download-v2-hero-title-2') }}</em></h1>
      <p class="sub">
        {{ $t('download-v2-hero-sub') }}
      </p>
      <div class="cta-row">
        <a class="store" :href="APP_STORE_URL" target="_blank" rel="noopener">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M17.5 12.5c0-2.6 2.1-3.8 2.2-3.9-1.2-1.7-3-1.9-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.3 2.6 1.3-.1 1.8-.9 3.4-.9s2 .9 3.4.8c1.4 0 2.3-1.3 3.2-2.5 1-1.4 1.4-2.8 1.4-2.9-.1 0-2.7-1-2.7-4z M14.5 4.7c.7-.9 1.2-2.1 1.1-3.3-1 0-2.3.7-3 1.5-.7.8-1.3 2-1.1 3.2 1.1.1 2.3-.6 3-1.4z"></path></svg>
          <span><small>{{ $t('download-v2-store-download-from') }}</small><b>App Store</b></span>
        </a>
        <a class="store" :href="GOOGLE_PLAY_URL" target="_blank" rel="noopener">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M3.6 1.6l13 11.4-3.4 2.9L3.6 22.4c-.4.2-.8 0-.8-.5V2.1c0-.5.4-.7.8-.5z"></path></svg>
          <span><small>{{ $t('download-v2-store-download-from') }}</small><b>Google Play</b></span>
        </a>
      </div>
      <div class="proof">
        <span><span class="stars">★★★★★</span> <b>4.7</b> · {{ $t('download-v2-proof-reviews') }}</span>
        <span class="sep"></span>
        <span><b>{{ $t('download-v2-proof-trial-bold') }}</b> · {{ $t('download-v2-proof-trial-suffix') }}</span>
        <span class="sep"></span>
        <span>{{ $t('download-v2-proof-developed-prefix') }} <b>{{ $t('download-v2-proof-developed-bold') }}</b></span>
      </div>
    </section>

    <!-- ───────────── Vertical zig-zag features ───────────── -->
    <section class="stage">
      <div class="features-grid">

        <article class="feature">
          <div class="copy" data-num="01">
            <span class="num">{{ $t('download-v2-feature-1-num') }}</span>
            <h3>{{ $t('download-v2-feature-1-title') }}</h3>
            <p>{{ $t('download-v2-feature-1-desc') }}</p>
            <p class="note-important"><b>{{ $t('download-v2-feature-note-label') }}</b> {{ $t('download-v2-feature-1-note') }}</p>
          </div>
          <div class="visual"><div class="mini">
            <div class="mini-status">
              <span>17:37</span>
              <span class="right-icons">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="#1d1d1f"><rect x="0" y="5" width="2" height="3"></rect><rect x="3" y="3" width="2" height="5"></rect><rect x="6" y="1" width="2" height="7"></rect><rect x="9" y="0" width="2" height="8"></rect></svg>
                <span class="batt">74</span>
              </span>
            </div>
            <div class="chat-row">
              <span class="arrow">←</span>
              <span class="pen">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
              </span>
            </div>
            <div class="chat-bubble">
              {{ $t('download-v2-feature-1-chat') }}
            </div>
          </div></div>
        </article>

        <article class="feature">
          <div class="copy" data-num="02">
            <span class="num">{{ $t('download-v2-feature-2-num') }}</span>
            <h3>{{ $t('download-v2-feature-2-title') }}</h3>
            <p>{{ $t('download-v2-feature-2-desc') }}</p>
          </div>
          <div class="visual"><div class="mini">
            <div class="art-img" :style="{ backgroundImage: `url(${ARTICLE_IMG})` }"></div>
            <div class="art-tag">{{ $t('download-v2-feature-2-tag') }}</div>
            <div class="art-title">{{ $t('download-v2-feature-2-art-title') }}</div>
            <div class="art-body">{{ $t('download-v2-feature-2-art-body') }}</div>
          </div></div>
        </article>

        <article class="feature">
          <div class="copy" data-num="03">
            <span class="num">{{ $t('download-v2-feature-3-num') }}</span>
            <h3>{{ $t('download-v2-feature-3-title') }}</h3>
            <p>{{ $t('download-v2-feature-3-desc') }}</p>
          </div>
          <div class="visual"><div class="mini">
            <div class="test-title">{{ $t('download-v2-feature-3-tests-title') }}</div>
            <div class="test-row">
              <div class="heart"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-7-4.35-9.5-8.5C.5 8.5 3 4 7 4c2 0 3.5 1 5 3 1.5-2 3-3 5-3 4 0 6.5 4.5 4.5 8.5C19 16.65 12 21 12 21z"></path></svg></div>
              <div class="text"><b>{{ $t('download-v2-feature-3-test1-name') }}</b>{{ $t('download-v2-feature-3-test1-desc') }}</div>
              <div class="play">▶</div>
            </div>
            <div class="test-row">
              <div class="heart"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-7-4.35-9.5-8.5C.5 8.5 3 4 7 4c2 0 3.5 1 5 3 1.5-2 3-3 5-3 4 0 6.5 4.5 4.5 8.5C19 16.65 12 21 12 21z"></path></svg></div>
              <div class="text"><b>{{ $t('download-v2-feature-3-test2-name') }}</b>{{ $t('download-v2-feature-3-test2-desc') }}</div>
              <div class="play">▶</div>
            </div>
          </div></div>
        </article>

        <article class="feature">
          <div class="copy" data-num="04">
            <span class="num">{{ $t('download-v2-feature-4-num') }}</span>
            <h3>{{ $t('download-v2-feature-4-title') }}</h3>
            <p>{{ $t('download-v2-feature-4-desc') }}</p>
          </div>
          <div class="visual"><div class="mini">
            <div class="mini-status">
              <span>17:34</span>
              <span class="right-icons">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="#1d1d1f"><rect x="0" y="5" width="2" height="3"></rect><rect x="3" y="3" width="2" height="5"></rect><rect x="6" y="1" width="2" height="7"></rect><rect x="9" y="0" width="2" height="8"></rect></svg>
                <span class="batt">74</span>
              </span>
            </div>
            <div class="fact-greet"><b>{{ $t('download-v2-feature-4-greet') }}</b> 👋<div class="fact-sub">{{ $t('download-v2-feature-4-fact-sub') }}</div></div>
            <div class="fact-card">
              <div class="ico"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><path d="M14 2v6h6"></path><path d="M9 13l2 2 4-4"></path></svg></div>
              <div class="body">{{ $t('download-v2-feature-4-fact-body') }}</div>
            </div>
            <div class="fact-more">{{ $t('download-v2-feature-4-fact-more') }}</div>
          </div></div>
        </article>

        <article class="feature">
          <div class="copy" data-num="05">
            <span class="num">{{ $t('download-v2-feature-5-num') }}</span>
            <h3>{{ $t('download-v2-feature-5-title') }}</h3>
            <p>{{ $t('download-v2-feature-5-desc') }}</p>
          </div>
          <div class="visual"><div class="mini" style="padding:14px;">
            <img src="/static/v2/download/specialists.jpg" :alt="$t('download-v2-feature-5-alt')" style="width:100%; display:block; border-radius:14px; max-height:480px; object-fit:cover; object-position:top;">
          </div></div>
        </article>

      </div>
    </section>

    <!-- ───────────── App support (reused 1:1 from live /download) ───────────── -->
    <NumberedAppSupportSection hide-cta />

    <!-- ───────────── Testimonials ───────────── -->
    <section class="testimonials">
      <div class="head">
        <span class="kicker">{{ $t('download-v2-testimonials-kicker') }}</span>
        <h2>{{ $t('download-v2-testimonials-title-prefix') }} <b>Momfident</b></h2>
      </div>
      <div class="t-grid">
        <article class="t-card v1">
          <div class="qmark">“</div>
          <p class="quote">{{ $t('download-v2-testimonial-1-quote') }}</p>
          <div class="person">
            <div class="avatar"><img src="/static/testimonials/yoana_testimonial.jpeg" :alt="$t('download-v2-testimonial-1-name')"></div>
            <div class="meta"><b>{{ $t('download-v2-testimonial-1-name') }}</b><span>{{ $t('download-v2-testimonial-1-role') }}</span></div>
          </div>
        </article>
        <article class="t-card v2">
          <div class="qmark">“</div>
          <p class="quote">{{ $t('download-v2-testimonial-2-quote') }}</p>
          <div class="person">
            <div class="avatar"><img src="/static/testimonials/delyana.jpg" :alt="$t('download-v2-testimonial-2-name')"></div>
            <div class="meta"><b>{{ $t('download-v2-testimonial-2-name') }}</b><span>{{ $t('download-v2-testimonial-2-role') }}</span></div>
          </div>
        </article>
        <article class="t-card v3">
          <div class="qmark">“</div>
          <p class="quote">{{ $t('download-v2-testimonial-3-quote') }}</p>
          <div class="person">
            <div class="avatar"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="9" r="4"></circle><path d="M4 22a8 8 0 0116 0"></path></svg></div>
            <div class="meta"><b>{{ $t('download-v2-testimonial-3-name') }}</b><span>{{ $t('download-v2-testimonial-3-role') }}</span></div>
          </div>
        </article>
      </div>
    </section>

    <!-- ───────────── FAQ ───────────── -->
    <section class="faq">
      <div class="head">
        <span class="kicker">{{ $t('download-v2-faq-kicker') }}</span>
        <h2>{{ $t('download-v2-faq-title') }}</h2>
      </div>
      <div class="faq-list">
        <details class="faq-item"><summary>{{ $t('download-v2-faq-1-q') }}</summary><div class="body">{{ $t('download-v2-faq-1-a') }}</div></details>
        <details class="faq-item" open><summary>{{ $t('download-v2-faq-2-q') }}</summary><div class="body">{{ $t('download-v2-faq-2-a') }}</div></details>
        <details class="faq-item"><summary>{{ $t('download-v2-faq-3-q') }}</summary><div class="body">{{ $t('download-v2-faq-3-a') }}</div></details>
        <details class="faq-item"><summary>{{ $t('download-v2-faq-4-q') }}</summary><div class="body">{{ $t('download-v2-faq-4-a') }}</div></details>
        <details class="faq-item"><summary>{{ $t('download-v2-faq-5-q') }}</summary><div class="body">{{ $t('download-v2-faq-5-a') }}</div></details>
        <details class="faq-item"><summary>{{ $t('download-v2-faq-6-q') }}</summary><div class="body">{{ $t('download-v2-faq-6-a') }}</div></details>
        <details class="faq-item"><summary>{{ $t('download-v2-faq-7-q') }}</summary><div class="body">{{ $t('download-v2-faq-7-a') }}</div></details>
        <details class="faq-item"><summary>{{ $t('download-v2-faq-8-q') }}</summary><div class="body">{{ $t('download-v2-faq-8-a') }}</div></details>
        <details class="faq-item"><summary>{{ $t('download-v2-faq-9-q') }}</summary><div class="body">{{ $t('download-v2-faq-9-a') }}</div></details>
      </div>
      <div class="faq-more">
        <router-link :to="locale === 'en' ? '/en/faq' : '/faq'" class="faq-more__link">{{ $t('download-v2-faq-more-link') }} &rarr;</router-link>
      </div>
    </section>

    <!-- ───────────── Pricing ───────────── -->
    <section class="pricing">
      <div class="head">
        <span class="kicker">{{ $t('download-v2-pricing-kicker') }}</span>
        <h2>{{ $t('download-v2-pricing-title-prefix') }} <b>{{ $t('download-v2-pricing-title-bold') }}</b></h2>
        <p class="sub">{{ $t('download-v2-pricing-sub') }}</p>
      </div>
      <div class="plans">
        <article class="plan">
          <span class="label">{{ $t('download-v2-plan-free-label') }}</span>
          <h3>{{ $t('download-v2-plan-free-title') }}</h3>
          <p class="term">{{ $t('download-v2-plan-free-term') }}</p>
          <div class="price"><span class="num" style="font-size:38px;letter-spacing:-.5px;">{{ $t('download-v2-plan-free-price') }}</span></div>
          <div class="strike" style="text-decoration:none;">&nbsp;</div>
          <ul>
            <li>{{ $t('download-v2-plan-free-li-1') }}</li>
            <li>{{ $t('download-v2-plan-free-li-2') }}</li>
            <li>{{ $t('download-v2-plan-free-li-3') }}</li>
            <li>{{ $t('download-v2-plan-free-li-4') }}</li>
          </ul>
          <router-link class="cta" to="/download">{{ $t('download-v2-plan-cta') }}</router-link>
        </article>

        <article class="plan">
          <span class="label">{{ $t('download-v2-plan-starter-label') }}</span>
          <h3>{{ $t('download-v2-plan-starter-title') }}</h3>
          <p class="term">{{ $t('download-v2-plan-starter-term') }}</p>
          <div class="price"><span class="curr">€</span><span class="num">12.99</span></div>
          <div class="strike" style="text-decoration:none;">{{ $t('download-v2-plan-starter-strike') }}</div>
          <ul>
            <li>{{ $t('download-v2-plan-unlimited-aya') }}</li>
            <li>{{ $t('download-v2-plan-starter-strike') }}</li>
          </ul>
          <router-link class="cta" to="/download">{{ $t('download-v2-plan-cta') }}</router-link>
        </article>

        <article class="plan featured">
          <span class="badge">{{ $t('download-v2-plan-featured-badge') }}</span>
          <span class="label">{{ $t('download-v2-plan-featured-label') }}</span>
          <h3>{{ $t('download-v2-plan-featured-title') }}</h3>
          <p class="term">{{ $t('download-v2-plan-featured-term') }}</p>
          <div class="price"><span class="curr">€</span><span class="num">34.99</span></div>
          <div class="strike">{{ $t('download-v2-plan-featured-strike') }}</div>
          <ul>
            <li>{{ $t('download-v2-plan-unlimited-aya') }}</li>
            <li>{{ $t('download-v2-plan-featured-save') }}</li>
          </ul>
          <router-link class="cta" to="/download">{{ $t('download-v2-plan-cta') }}</router-link>
        </article>

        <article class="plan">
          <span class="label">{{ $t('download-v2-plan-best-label') }}</span>
          <h3>{{ $t('download-v2-plan-best-title') }}</h3>
          <p class="term">{{ $t('download-v2-plan-best-term') }}</p>
          <div class="price"><span class="curr">€</span><span class="num">59.99</span></div>
          <div class="strike">{{ $t('download-v2-plan-best-strike') }}</div>
          <ul>
            <li>{{ $t('download-v2-plan-unlimited-aya') }}</li>
            <li>{{ $t('download-v2-plan-best-save') }}</li>
          </ul>
          <router-link class="cta" to="/download">{{ $t('download-v2-plan-cta') }}</router-link>
        </article>
      </div>
      <p class="pricing-note">{{ $t('download-v2-pricing-note') }}</p>
    </section>

    <!-- ───────────── Final download CTA ───────────── -->
    <section class="download">
      <div class="download-card">
        <div class="copy">
          <div class="label">{{ $t('download-v2-final-label') }}</div>
          <h2>{{ $t('download-v2-final-title-prefix') }} <b>{{ $t('download-v2-final-title-bold') }}</b></h2>
          <p class="desc">{{ $t('download-v2-final-desc') }}</p>
          <div class="stores">
            <a class="store" :href="APP_STORE_URL" target="_blank" rel="noopener">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M17.5 12.5c0-2.6 2.1-3.8 2.2-3.9-1.2-1.7-3-1.9-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.3 2.6 1.3-.1 1.8-.9 3.4-.9s2 .9 3.4.8c1.4 0 2.3-1.3 3.2-2.5 1-1.4 1.4-2.8 1.4-2.9-.1 0-2.7-1-2.7-4z M14.5 4.7c.7-.9 1.2-2.1 1.1-3.3-1 0-2.3.7-3 1.5-.7.8-1.3 2-1.1 3.2 1.1.1 2.3-.6 3-1.4z"></path></svg>
              <span><small>{{ $t('download-v2-store-download-from') }}</small><b>App Store</b></span>
            </a>
            <a class="store" :href="GOOGLE_PLAY_URL" target="_blank" rel="noopener">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M3.6 1.6l13 11.4-3.4 2.9L3.6 22.4c-.4.2-.8 0-.8-.5V2.1c0-.5.4-.7.8-.5z"></path></svg>
              <span><small>{{ $t('download-v2-store-download-from') }}</small><b>Google Play</b></span>
            </a>
          </div>
        </div>
        <div class="phone-wrap">
          <img src="/static/v2/momfident_phone_screen.png" :alt="$t('download-v2-final-phone-alt')">
        </div>
      </div>
    </section>

    <!-- ───────────── Footer ───────────── -->
    <footer class="site-foot">
      <router-link to="/" class="logo-link"><img src="/static/v2/logo.png" alt="Momfident"></router-link>
      <div class="links">
        <router-link to="/about">{{ $t('download-v2-footer-about') }}</router-link>
        <router-link to="/privacy-policy">{{ $t('download-v2-footer-privacy') }}</router-link>
        <a href="mailto:contact@momfident.io">{{ $t('download-v2-footer-contact') }}</a>
      </div>
      <span>© 2026 Momfident</span>
    </footer>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { setLang } from '@/i18n';
import NumberedAppSupportSection from '@/components/Components/Report/NumberedAppSupportSection.vue';

const { locale } = useI18n();
const menuOpen = ref(false);

const APP_STORE_URL = 'https://apps.apple.com/app/6751100834';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.momfident.app';
const ARTICLE_IMG = '/static/v2/download/article.jpg';

function toggleLang() {
  setLang(locale.value === 'bg' ? 'en' : 'bg');
}

</script>

<style lang="scss" scoped>
*, *::before, *::after { box-sizing: border-box; }

.dl-page {
  font-family: 'Rubik', sans-serif;
  color: #6B6B9C;
  background:
    radial-gradient(80% 60% at 80% 0%,  #FFE4EA 0%, transparent 60%),
    radial-gradient(70% 60% at 10% 30%, #FBE9EC 0%, transparent 55%),
    linear-gradient(180deg, #FFF5F7 0%, #FEF5F5 60%, #FBE9EC 100%);
  min-height: 100vh;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}
.dl-container { max-width: 1280px; margin: 0 auto; padding: 0 48px; }

a, a:link, a:hover, a:active, a:visited { color: inherit; text-decoration: none; }

/* Top bar */
.topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 28px 48px;
  max-width: 1280px; margin: 0 auto;
}
.topbar .logo-link { display: inline-flex; }
.topbar img { height: 32px; }
.topbar .right { display: flex; align-items: center; gap: 22px; }
.topbar .lang {
  border: 1.5px solid #FC678B; color:#FC678B; background: transparent; padding: 6px 12px; border-radius: 999px;
  font-size: 12px; font-weight: 600; letter-spacing: .5px; cursor: pointer; font-family: inherit;
}
.topbar .nav-link { font-size: 14px; color:#6B6B9C; cursor: pointer; }
.topbar .nav-link:hover { color:#FC678B; }
.topbar .hamburger { display: none; background: none; border: none; padding: 0; cursor: pointer; align-items: center; position: relative; z-index: 1001; }
.mobile-menu { display: none; }
@media (max-width: 720px) {
  .mobile-menu {
    display: flex; flex-direction: column; align-items: flex-start; gap: 18px;
    position: fixed; top: 0; right: -100%; width: 100%; height: 100vh;
    background: #fff; z-index: 1000; padding: 80px 24px 24px;
    transition: right .3s ease; box-shadow: -2px 0 5px rgba(0,0,0,.1);
  }
  .mobile-menu.active { right: 0; }
  .mobile-menu a { font-size: 15px; color: #6B6B9C; width: 100%; padding-bottom: 14px; border-bottom: 1px solid #FBE9EC; }
  .mobile-menu .lang {
    border: 1.5px solid #FC678B; color:#FC678B; background: transparent; padding: 6px 12px; border-radius: 999px;
    font-size: 12px; font-weight: 600; letter-spacing: .5px; cursor: pointer; font-family: inherit;
  }
}

/* Hero */
.hero { padding: 40px 0 0; text-align: center; position: relative; }
.hero .eyebrow {
  display: inline-flex; align-items: center; gap: 10px;
  background: rgba(255,255,255,.7); border: 1px solid #F0D8DE;
  padding: 8px 16px; border-radius: 999px;
  font-size: 11px; letter-spacing: 1.8px; font-weight: 600; color:#FC678B;
  backdrop-filter: blur(6px);
  margin-bottom: 22px;
}
.hero .eyebrow .dot { width: 6px; height: 6px; border-radius:50%; background:#FC678B; box-shadow: 0 0 0 4px rgba(252,103,139,.18);}
.hero h1 {
  font-size: clamp(48px, 6.4vw, 96px);
  font-weight: 300; line-height: .98; letter-spacing: -1.5px;
  color: #FC678B; margin: 0 auto;
  max-width: 16ch;
  text-wrap: balance;
}
.hero h1 em {
  font-style: normal; font-weight: 500;
  color: #FC678B;
  background: linear-gradient(180deg, transparent 62%, rgba(252,103,139,.18) 62%);
  padding: 0 4px;
}
.hero .sub {
  max-width: 640px; margin: 28px auto 0;
  font-size: 17px; line-height: 1.55; color:#6B6B9C; font-weight: 400;
  text-wrap: pretty;
}
.hero .cta-row {
  display: inline-flex; gap: 14px; margin-top: 36px;
  flex-wrap: wrap; justify-content: center;
}
.hero .store {
  display:inline-flex; align-items:center; gap:10px;
  background:#1d1d1f; color:#fff; border-radius:14px; padding:12px 22px;
  font-size: 11px; line-height:1.1; text-decoration:none;
  transition: transform .2s ease, box-shadow .2s ease;
  box-shadow: 0 8px 20px rgba(29,29,31,.18);
}
.hero .store:hover { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(29,29,31,.22); }
.hero .store b { font-size: 15px; font-weight: 600; display:block; letter-spacing: .3px;}
.hero .store small { font-size:10px; opacity:.85; letter-spacing: .8px;}
.hero .proof {
  display: flex; align-items: center; justify-content: center; gap: 22px;
  margin-top: 22px; flex-wrap: wrap;
  font-size: 12px; color:#9999B8;
}
.hero .proof .stars { color:#FC678B; letter-spacing: 1px; font-size: 14px;}
.hero .proof b { color:#6B6B9C; font-weight: 600; }
.hero .proof span.sep { width:4px; height:4px; border-radius:50%; background:#E0BEC6; display:inline-block;}

/* Vertical zig-zag feature rows */
.stage { position: relative; margin: 80px auto 0; padding: 0 48px; max-width: 1180px; }
.features-grid { display: flex; flex-direction: column; gap: 140px; }
.feature {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 80px;
}
.feature .copy { display:flex; flex-direction:column; gap: 18px; position: relative; max-width: 460px; }
.feature:nth-child(even) .copy { justify-self: end; }
.feature .copy::before {
  content: attr(data-num);
  position: absolute;
  top: -90px; left: -12px;
  font-size: 220px; font-weight: 300; line-height: 1;
  color: #FC678B;
  opacity: .10;
  letter-spacing: -8px;
  pointer-events: none;
}
.feature .num {
  display: inline-flex; align-items: center; gap: 12px;
  font-size: 12px; color:#FC678B; font-weight: 700; letter-spacing: 2px;
  position: relative;
}
.feature .num::before { content:""; width: 36px; height: 1.5px; background:#FC678B; }
.feature h3 {
  font-size: clamp(28px, 3vw, 38px); font-weight: 400; color: #6B6B9C;
  line-height: 1.15; margin: 0;
  text-wrap: balance; letter-spacing: -.4px;
  position: relative;
}
.feature h3 b { font-weight: 500; color:#FC678B; }
.feature p {
  font-size: 16px; line-height: 1.6; color: #6B6B9C; margin: 0;
  max-width: 42ch; position: relative;
}
.feature .note-important { font-size: 13px; color:#8A8AB4; line-height: 1.5; background: rgba(252,103,139,.08); border:1px solid rgba(252,103,139,.16); border-radius: 14px; padding: 12px 16px; margin: 0; max-width: 42ch; }
.feature .note-important b { color:#FC678B; font-weight: 600; }

/* Visual side */
.feature .visual {
  position: relative;
  background: linear-gradient(160deg, #FFE4EA, #FBE9EC 60%, #F7D4DD);
  border-radius: 36px;
  padding: 36px;
  box-shadow:
    0 50px 80px -30px rgba(252,103,139,.35),
    0 12px 28px rgba(107,107,156,.12);
  max-width: 460px; justify-self: start;
  will-change: transform;
}
.feature:nth-child(even) .visual { justify-self: end; }
.feature .visual::before {
  content:""; position:absolute; inset:-1px; border-radius:36px;
  border: 1px solid rgba(255,255,255,.6); pointer-events:none;
}
.feature .visual::after {
  content:""; position:absolute; width: 120px; height: 120px; border-radius:50%;
  background: radial-gradient(circle, rgba(252,103,139,.35), transparent 70%);
  filter: blur(10px);
  z-index: 0;
}
.feature:nth-child(odd) .visual::after  { right: -40px; top: -40px; }
.feature:nth-child(even) .visual::after { left:  -40px; bottom: -40px; }
.feature .visual .mini { position: relative; z-index: 1; }

/* Even-row swap: mockup on the left */
.feature:nth-child(even) { direction: rtl; }
.feature:nth-child(even) > * { direction: ltr; }

/* Mini-mockups */
.mini {
  border-radius: 22px;
  background: #fff;
  padding: 18px 16px 16px;
  display: flex; flex-direction: column; gap: 12px;
  box-shadow: 0 18px 40px -10px rgba(107,107,156,.25);
}
.mini-status {
  display:flex; justify-content: space-between; align-items: center;
  font-size: 11px; color:#1d1d1f; font-weight: 500;
}
.mini-status .right-icons { display:flex; gap:4px; align-items:center; }
.mini-status .batt { background:#1d1d1f; color:#fff; font-size:9px; padding:1px 5px; border-radius:3px; font-weight:600;}

.chat-row { display:flex; justify-content:space-between; align-items:center; }
.chat-row .arrow { font-size: 18px; color:#6B6B9C; }
.chat-row .pen {
  width:28px; height:28px; border-radius: 8px; border:1px solid #EFE6F5;
  display:flex;align-items:center;justify-content:center; color:#6B6B9C;
}
.chat-bubble {
  background: #EEE9F5; color:#6B6B9C;
  font-size: 11px; line-height: 1.5;
  padding: 12px 14px; border-radius: 14px 14px 14px 4px;
  align-self: flex-start; max-width: 95%;
}

.art-img {
  height: 140px; border-radius: 14px;
  background-size: cover;
  background-position: center;
  overflow: hidden;
}
.art-tag { font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; color:#FC678B;}
.art-title { font-size: 12px; font-weight: 600; color:#6B6B9C; line-height: 1.35; }
.art-body { font-size: 10.5px; color:#9999B8; line-height: 1.5;}

.test-title { font-size: 12px; color:#6B6B9C; font-weight: 600; text-align:center; margin-bottom: 4px;}
.test-row {
  background:#F2EEF5; border-radius: 14px; padding: 10px 12px;
  display:flex; align-items:center; gap: 10px;
}
.test-row .heart {
  width:28px; height:28px; border-radius:50%; background:#fff;
  display:flex;align-items:center;justify-content:center; color:#FC678B; flex-shrink: 0;
}
.test-row .text { flex:1; font-size: 10px; color:#6B6B9C; line-height: 1.35;}
.test-row .text b { display:block; font-weight:600; font-size: 10.5px; }
.test-row .play {
  width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid #FC678B;
  display:flex;align-items:center;justify-content:center; color:#FC678B; font-size:9px;
}

.fact-greet { font-size: 11px; color: #6B6B9C; }
.fact-greet b { font-weight: 600; }
.fact-sub { font-size: 10px; color: #9999B8; margin-top: 2px;}
.fact-card { background:#EEE9F5; border-radius: 14px; padding: 12px; display:flex; gap:10px; align-items:flex-start;}
.fact-card .ico {
  width:30px;height:30px;border-radius:8px;background:#fff;flex-shrink:0;
  display:flex;align-items:center;justify-content:center; color:#FC678B;
}
.fact-card .body { font-size: 10px; color:#6B6B9C; line-height: 1.5;}
.fact-more { font-size:10px;color:#FC678B;font-weight:600; }

/* Final CTA section */
.download { margin: 160px auto 80px; padding: 0 48px; max-width: 1280px; position: relative; }
.download-card {
  position: relative;
  background: linear-gradient(140deg, #FC678B 0%, #FB8AA8 60%, #FDB3C5 100%);
  border-radius: 48px;
  padding: 80px 80px 0;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1.1fr .9fr;
  align-items: end;
  gap: 40px;
  color: #fff;
}
.download-card::before, .download-card::after {
  content:""; position:absolute; pointer-events:none; border-radius: 50%;
}
.download-card::before {
  width: 380px; height: 380px; right: -120px; top: -120px;
  background: radial-gradient(circle, rgba(255,255,255,.25), transparent 65%);
}
.download-card::after {
  width: 220px; height: 220px; left: 28%; bottom: -90px;
  background: radial-gradient(circle, rgba(255,255,255,.18), transparent 65%);
}
.download-card .copy { padding-bottom: 80px; position: relative; z-index: 2;}
.download-card .label {
  font-size: 11px; letter-spacing: 2px; font-weight: 700;
  color: rgba(255,255,255,.85); margin-bottom: 18px;
}
.download-card h2 {
  font-size: clamp(28px, 3.4vw, 44px); font-weight: 300; line-height: 1.02;
  color: #fff; margin: 0 0 22px; letter-spacing: -.8px; white-space: nowrap;
  text-wrap: balance;
}
.download-card h2 b { font-weight: 500; }
.download-card .desc {
  font-size: 16px; line-height: 1.55; color: rgba(255,255,255,.9);
  max-width: 38ch; margin: 0 0 32px;
}
.download-card .stores { display:flex; gap: 14px; flex-wrap: wrap;}
.download-card .store {
  display:inline-flex; align-items:center; gap:10px;
  background:#1d1d1f; color:#fff; border-radius:14px; padding:14px 24px;
  font-size: 11px; line-height:1.1; text-decoration:none;
  transition: transform .2s ease;
}
.download-card .store:hover { transform: translateY(-2px); }
.download-card .store b { font-size: 16px; font-weight: 600; display:block; letter-spacing: .3px;}
.download-card .store small { font-size:10px; opacity:.85; letter-spacing: .8px;}
.download-card .phone-wrap { position: relative; z-index: 2; display: flex; justify-content: center; }
.download-card .phone-wrap img {
  width: 100%; max-width: 320px; height: auto;
  transform: translateY(40px) rotate(-3deg);
  filter: drop-shadow(0 30px 60px rgba(0,0,0,.18));
}

/* Testimonials */
.testimonials { margin: 140px auto 0; padding: 0 48px; max-width: 1280px; }
.testimonials .head { text-align: center; max-width: 760px; margin: 0 auto 60px; }
.testimonials .kicker { display:inline-block; font-size: 11px; letter-spacing: 1.8px; font-weight: 700; color:#FC678B; margin-bottom: 14px; }
.testimonials h2 { font-size: clamp(34px, 4.2vw, 54px); font-weight: 300; color:#6B6B9C; line-height: 1.05; margin: 0; letter-spacing: -.6px; text-wrap: balance; }
.testimonials h2 b { font-weight: 500; color:#FC678B; }
.t-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
.t-card { position: relative; background: #fff; border-radius: 28px; padding: 40px 36px 32px; box-shadow: 0 1px 0 rgba(107,107,156,.06), 0 30px 60px -30px rgba(107,107,156,.18); display: flex; flex-direction: column; transition: transform .3s ease, box-shadow .3s ease; overflow: hidden; }
.t-card::before { content: ""; position: absolute; left: 0; top: 0; height: 4px; width: 100%; background: linear-gradient(90deg, var(--accent, #FC678B), transparent); opacity: .7; }
.t-card:hover { transform: translateY(-6px); box-shadow: 0 1px 0 rgba(107,107,156,.06), 0 40px 80px -30px rgba(252,103,139,.28); }
.t-card .qmark { font-family: Georgia, serif; font-size: 68px; line-height: .6; font-weight: 700; color: var(--accent, #FC678B); letter-spacing: -4px; margin: 0 0 18px; }
.t-card .quote { font-size: 15px; line-height: 1.65; color:#6B6B9C; font-weight: 400; margin: 0 0 28px; text-wrap: pretty; flex: 1; }
.t-card .person { display: grid; grid-template-columns: 48px 1fr; align-items: center; gap: 14px; padding-top: 24px; border-top: 1px solid #F0E4EA; }
.t-card .avatar { width: 48px; height: 48px; border-radius: 50%; background: linear-gradient(135deg, var(--accent, #FC678B), rgba(252,103,139,.4)); overflow: hidden; display: flex; align-items: center; justify-content: center; color: #fff; box-shadow: 0 6px 14px rgba(252,103,139,.22); }
.t-card .avatar svg { width: 26px; height: 26px; }
.t-card .avatar img { width: 100%; height: 100%; object-fit: cover; }
.t-card .meta b { display: block; font-size: 15px; font-weight: 600; color:#6B6B9C; letter-spacing: -.1px; }
.t-card .meta span { display: block; font-size: 12.5px; color:#9999B8; margin-top: 2px; }
.t-card.v1 { --accent: #6B6B9C; }
.t-card.v2 { --accent: #FC678B; }
.t-card.v3 { --accent: #FBB3C2; }

/* FAQ accordion */
.faq { margin: 140px auto 0; padding: 0 48px 140px; max-width: 1036px; }
.faq .head { text-align: center; margin: 0 auto 50px; }
.faq .kicker { display:inline-block; font-size: 11px; letter-spacing: 1.8px; font-weight: 700; color:#FC678B; margin-bottom: 14px; }
.faq h2 { font-size: clamp(34px, 4.2vw, 54px); font-weight: 300; color:#6B6B9C; line-height: 1.05; margin: 0; letter-spacing: -.6px; text-wrap: balance; }
.faq-list { display: flex; flex-direction: column; gap: 14px; }
.faq-more { text-align: center; margin-top: 26px; }
.faq-more__link { display: inline-block; font-size: 14px; font-weight: 600; color: #FC678B; text-decoration: none; }
.faq-more__link:hover { text-decoration: underline; }
.faq-item { border-radius: 22px; background: rgba(255,255,255,.55); border: 1px solid rgba(252,103,139,.14); overflow: hidden; transition: border-color .25s ease, background .25s ease, box-shadow .25s ease; }
.faq-item[open] { background: #fff; border-color: rgba(252,103,139,.28); box-shadow: 0 20px 40px -22px rgba(107,107,156,.22); }
.faq-item summary { list-style: none; cursor: pointer; padding: 22px 28px; display: flex; align-items: center; justify-content: space-between; gap: 20px; font-size: 16px; font-weight: 500; color:#6B6B9C; letter-spacing: -.1px; }
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item summary::after { content:""; width: 12px; height: 12px; border-right: 2px solid #FC678B; border-bottom: 2px solid #FC678B; transform: rotate(45deg); margin-top: -4px; transition: transform .25s ease; flex-shrink: 0; }
.faq-item[open] summary::after { transform: rotate(-135deg); margin-top: 4px; }
.faq-item .body { padding: 0 28px 24px; font-size: 14.5px; line-height: 1.65; color:#6B6B9C; }

/* Pricing */
.pricing { margin: 0 auto; padding: 0 48px; max-width: 1180px; }
.pricing .head { text-align: center; max-width: 760px; margin: 0 auto 60px; }
.pricing .kicker { display:inline-block; font-size: 11px; letter-spacing: 1.8px; font-weight: 700; color:#FC678B; margin-bottom: 14px; }
.pricing h2 { font-size: clamp(34px, 4.2vw, 54px); font-weight: 300; color:#6B6B9C; line-height: 1.05; margin: 0; letter-spacing: -.6px; text-wrap: balance; }
.pricing h2 b { font-weight: 500; color:#FC678B; }
.pricing .sub { margin: 18px auto 0; font-size: 15px; line-height: 1.6; color:#6B6B9C; max-width: 56ch; }
.plans { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; align-items: stretch; }
.plan { position: relative; background: #fff; border-radius: 28px; padding: 34px 26px 30px; border: 1px solid rgba(252,103,139,.12); display: flex; flex-direction: column; transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease; }
.plan:hover { transform: translateY(-4px); box-shadow: 0 30px 60px -30px rgba(252,103,139,.28); border-color: rgba(252,103,139,.28); }
.plan.featured { background: linear-gradient(180deg, #FFF5F7 0%, #FFE9EE 100%); border-color: #FC678B; box-shadow: 0 30px 60px -30px rgba(252,103,139,.32); }
.plan .badge { position: absolute; top: -14px; left: 50%; transform: translateX(-50%); background: #FC678B; color:#fff; font-size: 9.5px; font-weight: 700; letter-spacing: .8px; padding: 6px 12px; border-radius: 999px; text-transform: uppercase; white-space: nowrap; }
.plan .label { font-size: 11px; letter-spacing: 1.6px; font-weight: 700; color:#FC678B; opacity:.7; }
.plan h3 { font-size: 22px; font-weight: 600; color:#6B6B9C; margin: 10px 0 6px; letter-spacing: -.2px; }
.plan .term { font-size: 14px; color:#9999B8; margin: 0 0 22px; }
.plan .price { display: flex; align-items: baseline; gap: 8px; margin-bottom: 4px; }
.plan .price .num { font-size: 48px; font-weight: 300; color:#6B6B9C; letter-spacing: -1px; line-height: 1; }
.plan .price .curr { font-size: 22px; font-weight: 400; color:#6B6B9C; }
.plan .strike { font-size: 13px; color:#9999B8; text-decoration: line-through; margin-bottom: 18px; min-height: 1.2em; }
.plan ul { list-style: none; padding: 0; margin: 16px 0 26px; display: flex; flex-direction: column; gap: 10px; flex: 1; }
.plan ul li { font-size: 14px; color:#6B6B9C; line-height: 1.5; padding-left: 26px; position: relative; }
.plan ul li::before { content:""; position: absolute; left: 0; top: 6px; width: 14px; height: 8px; border-left: 2px solid #FC678B; border-bottom: 2px solid #FC678B; transform: rotate(-45deg); }
.plan .cta { display: block; text-align: center; padding: 14px 22px; border-radius: 999px; background: #6B6B9C; color: #fff; font-weight: 600; font-size: 14px; text-decoration: none; letter-spacing: .2px; transition: background .2s ease, transform .2s ease; }
.plan .cta:hover { transform: translateY(-2px); background: #5A5A8A; }
.plan.featured .cta { background: #FC678B; }
.plan.featured .cta:hover { background: #E5557A; }
.pricing-note { text-align: center; margin-top: 30px; font-size: 13px; color:#9999B8; }

/* Footer */
.site-foot {
  border-top: 1px solid #F0D8DE; padding: 40px 48px 60px;
  max-width: 1280px; margin: 0 auto;
  display:flex; align-items:center; justify-content:space-between;
  flex-wrap: wrap; gap: 18px;
  font-size: 12px; color:#9999B8;
}
.site-foot .logo-link { display: inline-flex; }
.site-foot img { height: 26px; }
.site-foot .links { display:flex; gap: 24px;}
.site-foot a { color:#9999B8; text-decoration:none;}
.site-foot a:hover { color:#FC678B; }

/* Responsive */
@media (max-width: 980px) {
  .features-grid { gap: 100px; }
  .feature { grid-template-columns: 1fr; gap: 36px; }
  .feature, .feature:nth-child(even) { direction: ltr; }
  .feature:nth-child(even) .copy,
  .feature:nth-child(even) .visual { justify-self: stretch; }
  .feature .visual { max-width: 480px; margin: 0 auto; }
  .feature .copy { max-width: none; }
  .feature .copy::before { font-size: 160px; top: -64px; }
  .download-card { grid-template-columns: 1fr; padding: 60px 40px 0; text-align:center;}
  .download-card h2 { white-space: normal; font-size: clamp(28px, 7vw, 40px); }
  .download-card .copy { padding-bottom: 0; }
  .download-card .desc { margin-left:auto; margin-right:auto;}
  .download-card .stores { justify-content: center;}
  .download-card .phone-wrap img { transform: translateY(30px) rotate(0); }
  .t-grid { grid-template-columns: 1fr; gap: 22px; }
  .testimonials { margin-top: 100px; }
  .plans { grid-template-columns: repeat(2, 1fr); gap: 20px; }
  .pricing { margin-top: 0; }
}
@media (max-width: 720px) {
  .topbar { padding: 20px 24px; }
  .topbar .right { display: none; }
  .topbar .hamburger { display: flex; }
  .dl-container, .stage, .download { padding-left: 24px; padding-right:24px; }
  .features-grid { gap: 80px; }
  .feature .visual { padding: 24px; border-radius: 28px; }
  .feature .copy::before { font-size: 120px; top: -48px; }
  .testimonials { padding: 0 24px; }
  .t-card { padding: 32px 26px 26px; border-radius: 24px;}
  .faq { padding: 0 24px 100px; margin-top: 100px; }
  .faq-item summary { padding: 18px 22px; font-size: 15px; }
  .faq-item .body { padding: 0 22px 20px; }
  .pricing { padding: 0 24px; }
  .plans { grid-template-columns: 1fr; }
  .site-foot { padding: 30px 24px 40px;}
}
</style>
